import get from 'get-value';
import Papa from 'papaparse';
import superagent from 'superagent';
import { EventEmitter } from 'events';

import { sleep, toQueryString } from 'shared/utils/utils';
import { EVENT_REQUEST_PROGRESS } from 'src/constants';
import office from 'src/services/office';
import { officeStore } from 'src/stores/index';

const UPLOAD_CONTENT_TYPE = 'application/json';

export const requestEmmiter = new EventEmitter();
let requestCounter = 0;

export function startRequest() {
  if (++requestCounter == 1) {
    requestEmmiter.emit(EVENT_REQUEST_PROGRESS, true);
  }
}

export function stopRequest() {
  if (requestCounter > 0) {
    if (--requestCounter == 0) {
      requestEmmiter.emit(EVENT_REQUEST_PROGRESS, false);
    }
  }
}

async function simpleRequest(method, uri, data, token) {
  // Workarround for make call from excel function that requres simple cors (later with shared runtime it could be removed)
  const headers = {
    'Content-Type': 'application/x-www-form-urlencoded',
  };

  const url = `${config.api.serverUrl}${uri}`;
  return requestImpl(method, url, headers, data, token);
}

async function request(method, uri, data, token, extraHeaders = {}) {
  const headers = {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache,no-store,must-revalidate,max-age=-1,private',
    'Pragma': 'no-cache',
    ...extraHeaders,
  };

  const url = `${config.api.serverUrl}${uri}`;
  return requestImpl(method, url, headers, data, token);
}

async function requestImpl(method, url, headers, data, token) {
  try {
    startRequest();

    const task = superagent(method, url);
    Object.keys(headers || {}).map(x => task.set(x, headers[x]));

    if (token) {
      task.set('Authorization', `Bearer ${token}`);
    }

    if (data) {
      method === 'GET' ? task.query(data) : task.send(data);
    }

    const result = await task;
    return result.body || result;
  }
  catch (error) {
    const { response } = error;
    if (response?.body?.code === 'sf:API_CURRENTLY_DISABLED') {
      throw new Error(
        'API_CURRENTLY_DISABLED: API is disabled for this User. ' +
        '<p>Please reach out to your Salesforce admin to enable API access to your account</p>'
      );
    }

    if (get(error, 'response.type') === 'application/json') {
      error.code = get(error, 'response.body.code') || 'unknown';
      error.message = get(error, 'response.body.message') || error.message;
    }

    if (error.status === 503 && error.code === 'maintenance_mode') {
      officeStore.maintenanceMode = true;
    }

    throw error;
  }
  finally {
    stopRequest();
  }
}

const schedulerHeaders = {
  'x-scheduler-client': 'timezone-v2',
};

// User

export function getRedirectionUrl(data) {
  const url = encodeURIComponent(data.url);
  return `${config.api.serverUrl}/redirect?url=${url}`;
}

export async function ms_authorize(data, token) {
  return request('GET', '/ms-authorize', data, token);
}

export async function ms_logout(data, token) {
  return request('GET', '/ms-logout', data, token);
}

export async function checkCompletion(data) {
  return simpleRequest('POST', '/check-completion', data);
}

export async function getUser(token, data) {
  return request('GET', '/user', data, token);
}

export async function patchUserSettings(data, token) {
  return request('PATCH', '/user', data, token);
}

// Scheduler

export async function fetchSchedulerAuth(data, token) {
  return request('GET', '/scheduler-auth', data, token);
}

export async function fetchSchedulerActions(data, token) {
  return request('GET', '/scheduler', data, token);
}

export async function fetchAllUserActions(token) {
  return request('GET', '/scheduler-user', {}, token);
}

export async function createSchedulerAction(data, token) {
  return request('POST', '/scheduler', data, token, schedulerHeaders);
}

export async function updateSchedulerAction(data, token) {
  return request('PATCH', '/scheduler', data, token, schedulerHeaders);
}

export async function updateSchedulerActions(data, token) {
  return request('PUT', '/scheduler', data, token, schedulerHeaders);
}

export async function updateSchedulerFlowsteps(data, token) {
  return request('PUT', '/scheduler-flowsteps', data, token);
}

export async function updateSchedulerFlows(data, token) {
  return request('PUT', '/scheduler-flows', data, token);
}

export async function deleteSchedulerActions(data, token) {
  return request('DELETE', '/scheduler', data, token, schedulerHeaders);
}

export async function logoutScheduler(data, token) {
  return request('POST', '/scheduler-logout', data, token);
}

export async function fetchDocumentLinks(data, token) {
  return request('POST', '/scheduler-document-links', data, token);
}

export async function openDocument(data, token) {
  return request('GET', '/scheduler-open-document', data, token);
}

export async function getSlackChannels(data, token) {
  return request('GET', '/slack-channels', data, token);
}

export async function slackLogout(token) {
  return request('POST', '/slack-logout', {}, token);
}

// Salesforce

export async function getSalesforceUrl(data, token) {
  return request('GET', '/sf-url', data, token);
}

export async function getManpackInstallation(token) {
  return request('GET', '/manpack-installation', {}, token);
}

export async function getManpackFlow(data, token) {
  return request('GET', '/manpack-registered-flow', data, token);
}

// Reports
export async function getReports(data, token) {
  return request('GET', '/reports', data, token);
}

export async function getReportData(data, token) {
  return request('GET', '/report-data', data, token);
}

export async function deleteReport(data, token) {
  return request('DELETE', '/report', data, token);
}


// Objects
export async function getObjects(data, token) {
  return request('GET', '/objects', data, token);
}

export async function getObjectData(data, token) {
  return request('GET', '/object-data', data, token);
}

export async function runQuery({ query, rowCountLimit }, token) {
  return simpleRequest('POST', '/runquery', { query, rowCountLimit, token });
}

// Flowsteps

export async function fetchFlowsteps(data, token) {
  return request('GET', '/flowsteps', data, token);
}

export async function startExecutingFlowstep(data, token, cancelToken) {
  const { completion } = await request('POST', '/excute-flowstep', data, token);
  const { url, timestamp } = await waitForCompletion(completion, cancelToken);
  return { url, timestamp };
}

export function processExecutedFlowstepResults(url, chunk, cancelToken) {
  return new Promise((resolve, reject) => {
    Papa.RemoteChunkSize = 1024 * 1024 * 2; // 2 MB
    // Papa.RemoteChunkSize = 1024 * 6; // 6 KB
    Papa.parse(url, {
      download: true,
      chunk: results => chunk(results.data, cancelToken),
      complete: resolve,
      error: error => error.message === 'Requested Range Not Satisfiable' ? resolve() : reject(error),
    });
  });
}

export async function executeFlowstep(data, token, chunk, cancelToken) {
  const { url, timestamp } = await startExecutingFlowstep(data, token, cancelToken);
  await processExecutedFlowstepResults(url, chunk, cancelToken);
  return timestamp;
}

export async function createWritableFile(data, token) {
  return request('POST', '/create-writable-file', data, token);
}

export async function uploadFlowstepData(stringifiedData, token, cancelToken) {
  const { key, url } = await createWritableFile({ contentType: UPLOAD_CONTENT_TYPE }, token);
  cancelToken.throwIfRequested();

  const headers = { 'Content-Type': UPLOAD_CONTENT_TYPE };
  await requestImpl('PUT', url, headers, stringifiedData);
  return key;
}

export async function waitForCompletion(key, cancelToken) {
  try {
    startRequest();

    let res = { status: 'pending' }
    do {
      cancelToken && cancelToken.throwIfRequested();
      await sleep(500);

      cancelToken && cancelToken.throwIfRequested();
      res = await checkCompletion({ key });
    } while (res && res.status === 'pending')

    if (res.status === 'fail') {
      const error = new Error(get(res, 'value.message') || 'Fail to complete operation');
      error.code = get(res, 'value.code') || 'unknown';
      throw error;
    };

    return res.value || null;
  }
  finally {
    stopRequest();
  }
}

export async function refreshToken(refreshUrl) {
  return request('GET', refreshUrl, {}, '');
}

// Misc

export async function emitDebug(data, token) {
  return request('POST', '/debug', data, token);
}
